import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Menu } from "@headlessui/react";
import { LockClosedIcon } from "@heroicons/react/outline";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "use-debounce";
import useComplianceAccess, {
  COMPLIANCE_ALLOWED_ROLE_LABELS,
} from "hooks/useComplianceAccess";
import { useComplianceContext } from "context/ComplianceContext";
import ComplianceSummaryGrid from "./ComplianceSummaryGrid";
import ComplianceTrackerTable from "./ComplianceTrackerTable";
import {
  formatDateDisplay,
  getTaskStatusBreakdown,
  computeSummaryFromTasks,
} from "./utils";
import ConfirmDialog from "../lib/Shared/ConfirmDialog";
import DeleteButtonWithConfirmation from "../lib/Shared/DeleteButtonWithConfirmation";
import TableActionMenuWrapper from "../lib/Shared/TableActionMenuWrapper";
import SearchInput from "../lib/Shared/SearchInput";
import ComplianceTemplateOverview from "./ComplianceTemplateOverview";
import TemplatesTable from "./TemplatesTable";
import SanctionListManager from "./SanctionListManager";
import SanctionListCronManager from "./SanctionListCronManager";
import { verifyForOrganization } from "state/slices/verifyInvite";
import orgAdminService from "services/orgAdminService";
import corporateDataService from "services/corporate";
import complianceService from "services/compliance";

const ALLOWED_VIEWS = new Set(["summary", "metrics", "tracker", "settings"]);

const DELETE_TEMPLATE_MODAL = "delete_template_modal";

const DEFAULT_SUPER_ADMIN_NAME = "Super Admin";
const DEFAULT_SUPER_ADMIN_CODE = "ORG001";
const ROLE_VALUE_KEYS = [
  "displayName",
  "label",
  "name",
  "title",
  "roleName",
  "value",
  "code",
  "slug",
  "role",
];

const normalizeRoleValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value)
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();
};

const normalizeString = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeString(item))
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  return String(value).trim();
};

const sanitizeRegistrationNumberForVerification = (value) => {
  const normalized = normalizeString(value);
  if (!normalized) {
    return "";
  }

  return normalized.replace(/\s+/g, "").toUpperCase();
};

const firstTruthyString = (...values) => {
  for (const value of values) {
    const normalized = normalizeString(value);
    if (normalized) {
      return normalized;
    }
  }
  return "";
};

const buildContactName = (contact) => {
  if (!contact) {
    return "";
  }

  if (typeof contact === "string") {
    return contact.trim();
  }

  const candidates = [
    contact.fullName,
    contact.name,
    contact?.names?.companyName,
    [
      contact?.names?.firstName,
      contact?.names?.middleName,
      contact?.names?.lastName,
    ],
    [contact.firstName, contact.middleName, contact.lastName],
  ];

  for (const candidate of candidates) {
    const normalized = normalizeString(candidate);
    if (normalized) {
      return normalized.replace(/\s+/g, " ");
    }
  }

  return "";
};

const buildContactProfile = (contact) => {
  if (!contact) {
    return null;
  }

  const name = buildContactName(contact);
  const email = firstTruthyString(
    contact?.electronicAddress?.emailAddress,
    contact?.emailAddress,
    contact?.email,
    contact?.contactEmail,
    contact?.contactPersonEmail
  );
  const phone = firstTruthyString(
    contact?.electronicAddress?.mobileNumber,
    contact?.mobileNumber,
    contact?.phoneNumber,
    contact?.phone,
    contact?.contactPhone,
    contact?.contactPersonPhone
  );

  if (!name && !email && !phone) {
    return null;
  }

  return {
    name: name || null,
    email: email || null,
    phone: phone || null,
  };
};

const normalizeCorporateRecord = (record) => {
  if (!record) {
    return null;
  }

  const id =
    firstTruthyString(
      record._id,
      record.id,
      record.corporateId,
      record.organizationId,
      record?.organization?._id,
      record?.organization?.id
    ) || "";

  if (!id) {
    return null;
  }

  const name =
    firstTruthyString(
      record?.names?.companyName,
      record.organizationName,
      record.companyName,
      record.businessName,
      record.name
    ) || id;

  const registrationNumber =
    firstTruthyString(
      record.corporateRegistrationNumber,
      record.registrationNumber,
      record.companyRegistrationNumber,
      record?.registration?.number,
      record?.registration?.registrationNumber
    ) || id.toUpperCase();

  const organizationName =
    firstTruthyString(
      record.organizationName,
      record?.organization?.name,
      record?.organization?.companyName,
      record.companyName,
      record.businessName,
      name
    ) || name;

  const organizationId =
    firstTruthyString(
      record.organizationId,
      record?.organization?._id,
      record?.organization?.id
    ) || null;

  const contact =
    record.contactPerson ||
    record.contactPersonDetails ||
    record.contactPersonInfo ||
    record.contact;

  const contactName = buildContactName(contact);

  const contactEmail =
    firstTruthyString(
      contact?.email,
      contact?.emailAddress,
      contact?.contactEmail,
      record.contactPersonEmail,
      record.email,
      record.contactEmail
    ) || null;

  const contactPhone =
    firstTruthyString(
      contact?.phoneNumber,
      contact?.contactPhone,
      contact?.phone,
      record.contactPersonPhone,
      record.phone
    ) || null;

  const corporateEmail =
    firstTruthyString(
      record?.company?.emailAddress,
      record?.company?.email,
      record?.emailAddress,
      record?.email,
      record?.companyEmail,
      contactEmail
    ) || null;

  const corporatePhone =
    firstTruthyString(
      record?.company?.mobileNumber,
      record?.company?.phoneNumber,
      record?.company?.telephoneNumber,
      record?.mobileNumber,
      record?.phoneNumber,
      record?.phone,
      contactPhone
    ) || null;

  const orgAdminProfile =
    contactName || contactEmail || contactPhone
      ? {
          name: contactName || null,
          email: contactEmail || null,
          phone: contactPhone || null,
        }
      : null;

  const corporateProfile =
    corporateEmail || corporatePhone
      ? {
          name: name || null,
          email: corporateEmail || null,
          phone: corporatePhone || null,
        }
      : null;

  return {
    id,
    name,
    registrationNumber,
    corporateRegistrationNumber: registrationNumber,
    organizationId,
    organizationName,
    orgAdminProfile,
    corporateProfile,
    companyName: name,
    companyEmail: corporateEmail,
    companyPhone: corporatePhone,
  };
};

const ALLOWED_ROLE_LOOKUP = (() => {
  const lookup = {};
  [...COMPLIANCE_ALLOWED_ROLE_LABELS, "Registrar"].forEach((label) => {
    const normalized = normalizeRoleValue(label);
    if (normalized) {
      lookup[normalized] = label;
    }
  });
  return lookup;
})();

const toTitleCase = (value = "") =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map(
      (word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
    )
    .join(" ");

const buildDisplayNameFromUser = (
  user,
  fallbackName = DEFAULT_SUPER_ADMIN_NAME
) => {
  if (!user) {
    return fallbackName;
  }

  if (typeof user.fullName === "string" && user.fullName.trim()) {
    return user.fullName.trim();
  }

  const names = user.names || {};

  if (typeof names.companyName === "string" && names.companyName.trim()) {
    return names.companyName.trim();
  }

  if (typeof names.fullName === "string" && names.fullName.trim()) {
    return names.fullName.trim();
  }

  const nameParts = [names.firstName, names.middleName, names.lastName]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean);

  if (nameParts.length) {
    return nameParts.map(toTitleCase).join(" ");
  }

  if (typeof user.name === "string" && user.name.trim()) {
    return user.name.trim();
  }

  const email = user.electronicAddress?.emailAddress || user.email;
  if (typeof email === "string" && email.trim()) {
    return email.trim();
  }

  return fallbackName;
};

const collectRoleValues = (role, seen = new WeakSet()) => {
  if (role === undefined || role === null) {
    return [];
  }

  if (typeof role === "string" || typeof role === "number") {
    return [role];
  }

  if (Array.isArray(role)) {
    return role.flatMap((item) => collectRoleValues(item, seen));
  }

  if (typeof role === "object") {
    if (seen.has(role)) {
      return [];
    }
    seen.add(role);

    return ROLE_VALUE_KEYS.flatMap((key) => collectRoleValues(role[key], seen));
  }

  return [];
};

const ORG_ADMIN_ROLE_VALUES = new Set([
  "ORG ADMIN",
  "ORGADMIN",
  "ORG ADMINISTRATOR",
  "ORGADMINISTRATOR",
  "ORGANIZATION ADMIN",
  "ORGANISATION ADMIN",
  "ORGANIZATION ADMINISTRATOR",
  "ORGANISATION ADMINISTRATOR",
  "DEFAULT ORG ADMIN",
  "DEFAULT ORGADMIN",
]);

const normalizeBooleanCandidate = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "yes") {
      return true;
    }
    if (normalized === "false" || normalized === "no") {
      return false;
    }
  }

  return null;
};

const collectRolesFromEntity = (entity) => {
  if (!entity) {
    return [];
  }

  const collected = [];
  [
    entity.roles,
    entity.roleAssignments,
    entity.dashboardRoles,
    entity.role,
  ].forEach((value) => {
    if (value !== undefined && value !== null) {
      collected.push(value);
    }
  });

  return collected;
};

const collectVerificationRoleCandidates = (payload) => {
  if (!payload) {
    return [];
  }

  const candidates = [];

  [
    payload.roles,
    payload.roleAssignments,
    payload.dashboardRoles,
    payload.role,
  ].forEach((value) => {
    if (value !== undefined && value !== null) {
      candidates.push(value);
    }
  });

  const userCandidates = [
    payload.user,
    payload.applicant,
    payload.registrar,
    payload.individual,
    payload.beneficialOwner,
  ];

  userCandidates.forEach((user) => {
    collectRolesFromEntity(user).forEach((value) => candidates.push(value));
    if (Array.isArray(user?.organizations)) {
      user.organizations.forEach((organization) => {
        collectRolesFromEntity(organization).forEach((value) =>
          candidates.push(value)
        );
      });
    }
    if (Array.isArray(user?.corporates)) {
      user.corporates.forEach((corporate) => {
        collectRolesFromEntity(corporate).forEach((value) =>
          candidates.push(value)
        );
      });
    }
  });

  const corporate = payload.corporate;
  if (corporate) {
    collectRolesFromEntity(corporate).forEach((value) =>
      candidates.push(value)
    );
    if (corporate.orgAdmin) {
      collectRolesFromEntity(corporate.orgAdmin).forEach((value) =>
        candidates.push(value)
      );
    }
    if (corporate.orgAdminProfile) {
      collectRolesFromEntity(corporate.orgAdminProfile).forEach((value) =>
        candidates.push(value)
      );
    }
    if (corporate.organization) {
      collectRolesFromEntity(corporate.organization).forEach((value) =>
        candidates.push(value)
      );
    }
  }

  if (Array.isArray(payload.organizations)) {
    payload.organizations.forEach((organization) => {
      collectRolesFromEntity(organization).forEach((value) =>
        candidates.push(value)
      );
    });
  }

  return candidates;
};

const hasOrgAdminRoleValue = (roles) =>
  collectRoleValues(roles).some((value) => {
    const normalized = normalizeRoleValue(value);
    return normalized && ORG_ADMIN_ROLE_VALUES.has(normalized);
  });

const isOrgAdminFromVerificationPayload = (payload) => {
  if (!payload) {
    return false;
  }

  const booleanIndicators = [
    payload.isOrgAdmin,
    payload.orgAdmin,
    payload.organizationAdmin,
    payload.isOrganizationAdmin,
    payload?.corporate?.isOrgAdmin,
    payload?.corporate?.orgAdmin,
    payload?.corporate?.organization?.isOrgAdmin,
    payload?.user?.isOrgAdmin,
    payload?.applicant?.isOrgAdmin,
    payload?.registrar?.isOrgAdmin,
    payload?.individual?.isOrgAdmin,
    payload?.beneficialOwner?.isOrgAdmin,
  ];

  for (const candidate of booleanIndicators) {
    const normalized = normalizeBooleanCandidate(candidate);
    if (normalized === true) {
      return true;
    }
  }

  const roleCandidates = collectVerificationRoleCandidates(payload);
  return roleCandidates.some((roles) => hasOrgAdminRoleValue(roles));
};

const extractComplianceRoleLabels = (roles = []) => {
  if (!Array.isArray(roles) || !roles.length) {
    return [];
  }

  const seen = new Set();
  const labels = [];

  roles.forEach((role) => {
    collectRoleValues(role).forEach((value) => {
      const normalized = normalizeRoleValue(value);
      if (
        normalized &&
        ALLOWED_ROLE_LOOKUP[normalized] &&
        !seen.has(normalized)
      ) {
        labels.push(ALLOWED_ROLE_LOOKUP[normalized]);
        seen.add(normalized);
      }
    });
  });

  return labels;
};

const deriveCorporateIdsFromUser = (user) => {
  if (!user) {
    return [];
  }

  const ids = new Set();
  const collect = (value) => {
    if (value === undefined || value === null) return;
    const normalized = String(value).trim();
    if (normalized) {
      ids.add(normalized);
    }
  };

  const sources = [];
  if (Array.isArray(user.corporates)) {
    sources.push(...user.corporates);
  }
  if (Array.isArray(user.organizations)) {
    sources.push(...user.organizations);
  }

  sources.forEach((entry) => {
    if (!entry) return;
    collect(entry.corporateId);
    collect(entry.corporate?.id);
    collect(entry.corporate?._id);
    collect(entry.id);
    collect(entry._id);
  });

  return Array.from(ids);
};

const buildSuperAdminDetails = ({
  currentUser,
  fallbackSummary,
  fallbackProfile,
  aggregatedRoles,
  hasRegistrarRole,
  hasUploaderRole,
  isOrgOrSuperAdmin,
}) => {
  const summary = fallbackSummary || {};
  const profile = fallbackProfile || {};

  const displayName = buildDisplayNameFromUser(
    currentUser,
    summary?.name || DEFAULT_SUPER_ADMIN_NAME
  );

  const complianceRoleLabels = extractComplianceRoleLabels(aggregatedRoles);

  const corporateIdsFromUser = deriveCorporateIdsFromUser(currentUser);
  const combinedCorporateIds = [
    ...(Array.isArray(summary?.corporateIds) ? summary.corporateIds : []),
    ...(Array.isArray(profile?.corporateIds) ? profile.corporateIds : []),
    ...corporateIdsFromUser,
  ];
  const corporateIds = Array.from(
    new Set(combinedCorporateIds.filter((value) => !!value))
  );

  const codeCandidates = [
    currentUser?.code,
    currentUser?.employeeCode,
    currentUser?.orgAdminCode,
    currentUser?.staffId,
    profile?.code,
    summary?.code,
  ]
    .map((value) =>
      value !== undefined && value !== null ? String(value).trim() : ""
    )
    .filter(Boolean);
  const resolvedCode = codeCandidates[0] || DEFAULT_SUPER_ADMIN_CODE;

  const idCandidates = [
    currentUser?._id,
    currentUser?.id,
    profile?.id,
    summary?.id,
  ]
    .map((value) =>
      value !== undefined && value !== null ? String(value).trim() : ""
    )
    .filter(Boolean);
  const resolvedId = idCandidates[0] || "super-admin";

  const emailCandidates = [
    currentUser?.electronicAddress?.emailAddress,
    currentUser?.email,
    summary?.email,
  ]
    .map((value) =>
      value !== undefined && value !== null ? String(value).trim() : ""
    )
    .filter(Boolean);
  const resolvedEmail = emailCandidates[0] || "";

  let roleDescriptor = "";
  if (hasRegistrarRole) {
    roleDescriptor = "Registrar";
  } else if (complianceRoleLabels.length) {
    roleDescriptor = "Employee";
  } else if (isOrgOrSuperAdmin) {
    roleDescriptor = DEFAULT_SUPER_ADMIN_NAME;
  }

  const identity = {
    id: resolvedId,
    name: displayName,
    code: resolvedCode,
    email: resolvedEmail,
    roleDescriptor,
    complianceRoles: complianceRoleLabels,
    hasRegistrarRole,
    hasUploaderRole,
    isOrgOrSuperAdmin,
    corporateIds,
  };

  return {
    identity,
    summary: {
      ...summary,
      id: resolvedId,
      name: displayName,
      code: resolvedCode,
      email: resolvedEmail,
      corporateIds,
      roleDescriptor,
      complianceRoles: complianceRoleLabels,
    },
    profile: {
      ...profile,
      id: resolvedId,
      code: resolvedCode,
      corporateIds,
    },
  };
};

const DELETE_ORG_ADMIN_ASSIGNMENT_MODAL =
  "delete_org_admin_assignment_confirmation_modal";

const ORG_ADMIN_CODE_PREFIX = "ORG";
const ORG_ADMIN_CODE_PATTERN = /^([A-Z]+)(\d{3,})$/;
const DEFAULT_CORPORATE_RESULTS = 6;
const CORPORATE_SEARCH_MIN_LENGTH = 2;
const CORPORATE_SEARCH_LIMIT = 10;

const buildOrgAdminFormState = (values = {}) => ({
  name: values?.name || "",
  code: values?.code || "",
  email: values?.email || "",
  phone: values?.phone || "",
  organizationId: values?.organizationId || "",
  corporateId: values?.corporateId || "",
  templateId: values?.templateId || "",
  companyName: values?.companyName || "",
  companyRegistrationNumber: values?.companyRegistrationNumber || "",
});

const createEmptyTemplateDraft = () => ({ name: "", description: "" });

const generateNextOrgAdminCode = (
  assignments,
  prefix = ORG_ADMIN_CODE_PREFIX
) => {
  const safeAssignments = Array.isArray(assignments) ? assignments : [];

  const maxNumber = safeAssignments.reduce((largest, assignment) => {
    const candidate = (assignment?.code || "").trim().toUpperCase();
    const match = candidate.match(ORG_ADMIN_CODE_PATTERN);

    if (!match || match[1] !== prefix) {
      return largest;
    }

    const parsed = Number.parseInt(match[2], 10);
    if (Number.isNaN(parsed)) {
      return largest;
    }

    return Math.max(largest, parsed);
  }, 0);

  const nextNumber = maxNumber + 1;
  return `${prefix}${String(nextNumber).padStart(3, "0")}`;
};

const buildTrackerEntities = (
  profile,
  corporateSummaries,
  companySummaries,
  listCorporateTasks,
  listCompanyTasks
) => {
  if (!profile) return [];

  const corporateEntities = (profile.corporateIds || []).map((corporateId) => {
    const corporateSummary =
      corporateSummaries.find((item) => item.id === corporateId) || {};

    return {
      id: corporateId,
      name: corporateSummary.name || corporateId,
      label: "Corporate",
      tasks: listCorporateTasks(corporateId),
      promptMonth: corporateSummary.promptMonth,
      annualReturnDate: corporateSummary.annualReturnDate,
    };
  });

  const companyEntities = (profile.companyIds || []).map((companyId) => {
    const companySummary =
      companySummaries.find((item) => item.id === companyId) || {};

    return {
      id: companyId,
      name: companySummary.name || companyId,
      label: "Company",
      tasks: listCompanyTasks(companyId),
      promptMonth: companySummary.promptMonth,
      annualReturnDate: companySummary.annualReturnDate,
    };
  });

  return [...corporateEntities, ...companyEntities];
};

const SummaryView = ({ displayName, orgAdminCount, tasks }) => {
  const mandatoryTasks = React.useMemo(
    () => (tasks || []).filter((t) => t && t.isMandatory === true),
    [tasks]
  );
  const counts = React.useMemo(() => {
    return (mandatoryTasks || []).reduce(
      (acc, t) => {
        acc.total += 1;
        const k = t.summaryStatus;
        if (k === "compliant") acc.compliant += 1;
        else if (k === "notDue") acc.notDue += 1;
        else if (k === "due") acc.due += 1;
        else if (k === "notCompliant") acc.notCompliant += 1;
        return acc;
      },
      { total: 0, compliant: 0, notDue: 0, due: 0, notCompliant: 0 }
    );
  }, [mandatoryTasks]);
  const summary = React.useMemo(() => {
    const total = counts.total || 0;
    if (total === 0) {
      return { compliant: 0, notDue: 0, due: 0, notCompliant: 0 };
    }
    const p = (v) => Math.round((v / total) * 100 * 100) / 100;
    return {
      compliant: p(counts.compliant),
      notDue: p(counts.notDue),
      due: p(counts.due),
      notCompliant: p(counts.notCompliant),
    };
  }, [counts]);
  return (
    <section className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <header className="border-b border-gray-200 pb-4">
        <p className="text-xs uppercase tracking-wide text-gray-500">
          SuperAdmin Dashboard
        </p>
        <div className="mt-1 flex flex-col gap-2 md:flex-row md:items-baseline md:justify-between">
          <div>
            <p className="text-sm text-gray-600">
              Aggregated across {orgAdminCount || 0} Organization(s).
            </p>
          </div>
        </div>
      </header>
      <ComplianceSummaryGrid
        summary={summary}
        counts={{
          compliant: counts.compliant,
          notDue: counts.notDue,
          due: counts.due,
          notCompliant: counts.notCompliant,
        }}
        caption="Percentages are based on mandatory tasks across org admins"
      />
    </section>
  );
};

SummaryView.propTypes = {
  displayName: PropTypes.string,
  orgAdminCount: PropTypes.number,
  tasks: PropTypes.array,
};

SummaryView.defaultProps = {
  displayName: undefined,
  orgAdminCount: 0,
  tasks: undefined,
};

const MetricsView = ({
  assignedAdmins,
  corporates,
  listCorporateTasks,
  listCompanyTasks,
}) => {
  const adminRows = useMemo(() => {
    const rows = [];
    (assignedAdmins || []).forEach((admin) => {
      const corporateId = admin?.corporateId;
      const corporate =
        (corporates || []).find(
          (c) => String(c?.id || c?._id) === String(corporateId)
        ) || null;

      const associatedIds = [];
      if (corporate) {
        const ids =
          Array.isArray(corporate.associatedCompanyIds) &&
          corporate.associatedCompanyIds.length > 0
            ? corporate.associatedCompanyIds
            : Array.isArray(corporate.associatedCompanies)
            ? corporate.associatedCompanies
                .map((co) => co?._id || co?.id)
                .filter(Boolean)
            : [];
        associatedIds.push(
          ...ids.map((v) => {
            const s = String(v);
            return s;
          })
        );
      }

      const tasks = [];
      if (corporateId) {
        const corpTasks = listCorporateTasks(corporateId) || [];
        tasks.push(...corpTasks);
      }
      associatedIds.forEach((companyId) => {
        const companyTasks = listCompanyTasks(companyId) || [];
        tasks.push(...companyTasks);
      });

      const breakdown = getTaskStatusBreakdown(tasks);
      const total = breakdown.total || 0;
      const pct = (count) => (total > 0 ? (count / total) * 100 : 0);

      rows.push({
        name: admin?.companyName || admin?.name || String(corporateId || ""),
        total,
        compliant: {
          count: breakdown.compliant,
          pct: pct(breakdown.compliant),
        },
        notDue: { count: breakdown.notDue, pct: pct(breakdown.notDue) },
        due: { count: breakdown.due, pct: pct(breakdown.due) },
        notCompliant: {
          count: breakdown.notCompliant,
          pct: pct(breakdown.notCompliant),
        },
      });
    });
    return rows;
  }, [assignedAdmins, corporates, listCorporateTasks, listCompanyTasks]);

  const formatCell = (count, percentage) =>
    `${count} (${Number(percentage ?? 0).toFixed(2)}%)`;

  return (
    <section className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <header className="border-b border-gray-200 pb-4">
        <h3 className="text-xl font-semibold text-gray-900">
          Org Admin Compliance Metrics
        </h3>
      </header>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2">Org Admin</th>
              <th className="px-4 py-2 text-right">Active Tasks</th>
              <th className="px-4 py-2 text-right">Compliant</th>
              <th className="px-4 py-2 text-right">Not Due</th>
              <th className="px-4 py-2 text-right">Due</th>
              <th className="px-4 py-2 text-right">Not Compliant</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {adminRows.length === 0 ? (
              <tr>
                <td className="px-4 py-3 text-sm text-gray-500" colSpan={6}>
                  No org admins with assigned templates.
                </td>
              </tr>
            ) : (
              adminRows.map((row) => (
                <tr key={row.name} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-700">
                    {row.name}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">
                    {row.total}
                  </td>
                  <td className="px-4 py-2 text-right text-emerald-700">
                    {formatCell(row.compliant.count, row.compliant.pct)}
                  </td>
                  <td className="px-4 py-2 text-right text-emerald-700">
                    {formatCell(row.notDue.count, row.notDue.pct)}
                  </td>
                  <td className="px-4 py-2 text-right text-amber-700">
                    {formatCell(row.due.count, row.due.pct)}
                  </td>
                  <td className="px-4 py-2 text-right text-red-600">
                    {formatCell(row.notCompliant.count, row.notCompliant.pct)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

MetricsView.propTypes = {
  profile: PropTypes.shape({
    code: PropTypes.string,
    tasks: PropTypes.array,
    name: PropTypes.string,
    roleDescriptor: PropTypes.string,
  }),
  summary: PropTypes.object,
};

MetricsView.defaultProps = {
  profile: undefined,
  summary: undefined,
};

const TrackerView = ({ trackerEntities, superAdminName }) => (
  <section className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
    <header className="border-b border-gray-200 pb-4">
      <h3 className="text-xl font-semibold text-gray-900">
        Compliance Tracker
      </h3>
      <p className="text-sm text-gray-600">
        Deadlines mapped by corporate and task across the{" "}
        {superAdminName || DEFAULT_SUPER_ADMIN_NAME}'s portfolio.
      </p>
    </header>
    <ComplianceTrackerTable
      entities={trackerEntities}
      formatDate={formatDateDisplay}
      maxTaskSelections={12}
    />
  </section>
);

TrackerView.propTypes = {
  trackerEntities: PropTypes.array.isRequired,
  superAdminName: PropTypes.string,
};

TrackerView.defaultProps = {
  superAdminName: undefined,
};

const TemplateForm = ({ templateDraft, onChange, onSubmit, onCancel }) => (
  <form
    onSubmit={onSubmit}
    className="grid gap-4 rounded-xl border border-dashed border-sflPrimary/40 bg-slate-50 p-4 md:grid-cols-[2fr,3fr]"
  >
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Template Name
      </label>
      <input
        type="text"
        value={templateDraft.name}
        onChange={(event) =>
          onChange((previous) => ({ ...previous, name: event.target.value }))
        }
        placeholder="Annual Compliance Tracker"
        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
        required
      />
    </div>
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Description
      </label>
      <textarea
        value={templateDraft.description}
        onChange={(event) =>
          onChange((previous) => ({
            ...previous,
            description: event.target.value,
          }))
        }
        rows={2}
        placeholder="Outline the obligations covered by this template."
        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
      />
    </div>
    <div className="flex items-center justify-end gap-3 md:col-span-2">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sflPrimary/40 focus:ring-offset-1"
      >
        Cancel
      </button>
      <button
        type="submit"
        className="rounded-md bg-sflPrimary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2"
      >
        Save Template
      </button>
    </div>
  </form>
);

TemplateForm.propTypes = {
  templateDraft: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const OrgAdminAssignmentsTable = ({
  assignments,
  allTemplates,
  corporates,
  onView,
  onEdit,
  isLoading,
}) => {
  const corporateMap = useMemo(() => {
    const map = new Map();
    (corporates || []).forEach((corporate) => {
      if (!corporate?.id) {
        return;
      }

      map.set(corporate.id, {
        name: corporate.name || corporate.id,
        registrationNumber:
          corporate.registrationNumber || corporate.id.toUpperCase(),
      });
    });

    return map;
  }, [corporates]);

  const templateMap = useMemo(() => {
    const map = new Map();
    (allTemplates || []).forEach((template) => {
      if (template?.id) {
        map.set(template.id, {
          name: template.name,
          version: template.version,
        });
      }
    });
    return map;
  }, [allTemplates]);

  // Show loading state or empty state only after data has been fetched
  if (isLoading) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
          <span>Loading assignments...</span>
        </div>
      </div>
    );
  }

  if (!assignments || !Array.isArray(assignments) || !assignments.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
        No Org Admin assignments yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2">Org Admin</th>
            <th className="px-4 py-2">Registration Number</th>
            <th className="px-4 py-2">Template</th>
            <th className="px-4 py-2">Org Admin Code</th>
            <th className="px-4 py-2 text-right">Last Updated</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {assignments.map((assignment) => {
            const templateDetails = assignment.templateId
              ? templateMap.get(assignment.templateId)
              : null;

            // Use values from assignment object first (backend-provided), fallback to lookup map
            const templateName =
              assignment.templateName || templateDetails?.name || "";
            const templateVersion =
              assignment.templateVersion || templateDetails?.version || "";

            const templateLabel = templateName
              ? `${templateName}${
                  templateVersion ? ` (v${templateVersion})` : ""
                }`
              : "—";

            const corporateDetails = assignment.corporateId
              ? corporateMap.get(assignment.corporateId)
              : null;
            const orgAdminLabel =
              assignment.companyName || corporateDetails?.name || "—";
            const registrationLabel =
              assignment.companyRegistrationNumber ||
              corporateDetails?.registrationNumber ||
              "—";

            const codeLabel =
              assignment.code ||
              assignment.orgAdminCode ||
              (assignment.metadata && assignment.metadata.orgAdminCode) ||
              (assignment.user &&
                (assignment.user.orgAdminCode || assignment.user.code)) ||
              "—";
            return (
              <tr key={assignment.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-700">
                  {orgAdminLabel}
                </td>
                <td className="px-4 py-2 text-gray-600">{registrationLabel}</td>
                <td className="px-4 py-2 text-gray-600">{templateLabel}</td>
                <td className="px-4 py-2 text-gray-600">{codeLabel}</td>
                <td className="px-4 py-2 text-right text-gray-500">
                  {formatDateDisplay(
                    assignment.updatedAt || assignment.assignedAt
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <TableActionMenuWrapper>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={() => onView(assignment)}
                          className={`${
                            active
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700"
                          } block w-full px-4 py-2 text-left text-sm`}
                        >
                          View
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={() => onEdit(assignment)}
                          className={`${
                            active
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700"
                          } block w-full px-4 py-2 text-left text-sm`}
                        >
                          Edit
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <DeleteButtonWithConfirmation
                          row={assignment}
                          confirmationModalName={
                            DELETE_ORG_ADMIN_ASSIGNMENT_MODAL
                          }
                          btnLabel="Delete"
                          overrideBaseStyles
                          btnClasses={`${
                            active ? "bg-red-50 text-red-600" : "text-red-600"
                          } block w-full px-4 py-2 text-left text-sm`}
                        />
                      )}
                    </Menu.Item>
                  </TableActionMenuWrapper>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

OrgAdminAssignmentsTable.propTypes = {
  assignments: PropTypes.array.isRequired,
  allTemplates: PropTypes.array.isRequired,
  corporates: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

OrgAdminAssignmentsTable.defaultProps = {
  isLoading: false,
  allTemplates: [],
};

const OrgAdminAssignmentForm = ({
  mode,
  initialValues,
  templates,
  allTemplates,
  corporates,
  availableCorporates,
  existingAssignments,
  onSubmit,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const isViewOnly = mode === "view";
  const currentCorporateId =
    normalizeString(initialValues?.corporateId) || null;

  const [formState, setFormState] = useState(() =>
    buildOrgAdminFormState(initialValues)
  );
  const [corporateQuery, setCorporateQuery] = useState(() => {
    if (initialValues?.corporateId) {
      const corporate = corporates.find(
        (corp) => normalizeString(corp.id) === currentCorporateId
      );
      if (corporate?.companyRegistrationNumber) {
        return corporate.companyRegistrationNumber;
      }
      if (corporate?.registrationNumber) {
        return corporate.registrationNumber;
      }
    }

    return (
      normalizeString(initialValues?.companyRegistrationNumber) ||
      normalizeString(initialValues?.companyRegNumber) ||
      ""
    );
  });
  const [selectedCorporateId, setSelectedCorporateId] = useState(
    currentCorporateId || ""
  );
  const [showCorporateOptions, setShowCorporateOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remoteCorporates, setRemoteCorporates] = useState([]);
  const [isCorporateSearchLoading, setIsCorporateSearchLoading] =
    useState(false);
  const [corporateSearchError, setCorporateSearchError] = useState(null);
  const [isCorporateDetailsLoading, setIsCorporateDetailsLoading] =
    useState(false);
  const [assignableStatus, setAssignableStatus] = useState({});
  const [notOrgAdminError, setNotOrgAdminError] = useState("");

  const [debouncedCorporateQuery] = useDebounce(corporateQuery, 400);

  const { items: organizations } = useSelector(
    (state) => state.organizationsSlice
  ).organizations;

  const availableOrganizations = useMemo(() => organizations, [organizations]);

  const resolveOrgAdminExistence = useCallback((payload) => {
    if (payload === undefined || payload === null) {
      return false;
    }

    if (typeof payload === "boolean") {
      return payload;
    }

    if (Array.isArray(payload)) {
      if (payload.length === 0) {
        return false;
      }

      return payload.some((item) => resolveOrgAdminExistence(item));
    }

    if (typeof payload === "object") {
      if (payload.data !== undefined) {
        return resolveOrgAdminExistence(payload.data);
      }

      if (payload.exists !== undefined) {
        return Boolean(payload.exists);
      }

      if (payload.isOrgAdmin !== undefined) {
        return Boolean(payload.isOrgAdmin);
      }

      if (
        payload.orgAdminProfile ||
        payload.name ||
        payload.email ||
        payload.phone ||
        payload.code
      ) {
        return true;
      }
    }

    return false;
  }, []);

  const markAssignableStatus = useCallback((corporateId, value) => {
    const normalizedId = normalizeString(corporateId);
    if (!normalizedId) {
      return;
    }

    setAssignableStatus((previous) => {
      if (previous[normalizedId] === value) {
        return previous;
      }
      return { ...previous, [normalizedId]: value };
    });
  }, []);

  const isOptionAssignable = useCallback(
    (option) => {
      if (!option || !option.id) {
        return false;
      }

      if (option.isAssigned) {
        return true;
      }

      if (option.orgAdminProfile) {
        return true;
      }

      const normalizedId = normalizeString(option.id);
      if (!normalizedId) {
        return false;
      }

      const status = assignableStatus[normalizedId];
      if (typeof status === "boolean") {
        return status;
      }

      return false;
    },
    [assignableStatus]
  );

  const assignedCorporateIds = useMemo(() => {
    const ids = new Set();
    existingAssignments.forEach((assignment) => {
      const normalizedId = normalizeString(assignment?.corporateId);
      if (
        normalizedId &&
        normalizedId !== currentCorporateId &&
        !ids.has(normalizedId)
      ) {
        ids.add(normalizedId);
      }
    });
    return ids;
  }, [existingAssignments, currentCorporateId]);

  useEffect(() => {
    if (currentCorporateId) {
      markAssignableStatus(currentCorporateId, true);
    }
  }, [currentCorporateId, markAssignableStatus]);

  const corporateOptions = useMemo(() => {
    const map = new Map();
    const isSearching =
      !isViewOnly &&
      typeof corporateQuery === "string" &&
      corporateQuery.trim().length >= CORPORATE_SEARCH_MIN_LENGTH;

    const source = isSearching
      ? [] // when searching, rely only on remote results from backend
      : mode === "create" && Array.isArray(availableCorporates)
      ? availableCorporates
      : corporates;

    source.forEach((corporate) => {
      const normalizedRecord = normalizeCorporateRecord(corporate) || {};
      const normalizedId = normalizeString(
        normalizedRecord.id || corporate?.id
      );
      if (!normalizedId) {
        return;
      }

      map.set(normalizedId, {
        id: normalizedId,
        name:
          normalizeString(normalizedRecord.name) ||
          normalizeString(corporate?.name) ||
          normalizedId,
        registrationNumber:
          normalizeString(normalizedRecord.registrationNumber) ||
          normalizeString(corporate?.registrationNumber) ||
          normalizedId.toUpperCase(),
        corporateRegistrationNumber:
          normalizeString(normalizedRecord.corporateRegistrationNumber) ||
          normalizeString(corporate?.corporateRegistrationNumber) ||
          normalizeString(corporate?.registrationNumber) ||
          normalizedId.toUpperCase(),
        organizationId:
          normalizeString(normalizedRecord.organizationId) ||
          normalizeString(corporate?.organizationId) ||
          null,
        organizationName:
          normalizeString(normalizedRecord.organizationName) ||
          normalizeString(corporate?.organizationName) ||
          normalizeString(corporate?.name) ||
          normalizedId,
        orgAdminProfile:
          normalizedRecord.orgAdminProfile ||
          corporate?.orgAdminProfile ||
          null,
        corporateProfile:
          normalizedRecord.corporateProfile ||
          corporate?.corporateProfile ||
          null,
        companyName:
          normalizeString(normalizedRecord.companyName) ||
          normalizeString(corporate?.companyName) ||
          normalizeString(corporate?.name) ||
          normalizedId,
        companyEmail:
          normalizedRecord.companyEmail ||
          corporate?.companyEmail ||
          normalizedRecord.orgAdminProfile?.email ||
          corporate?.orgAdminProfile?.email ||
          null,
        companyPhone:
          normalizedRecord.companyPhone ||
          corporate?.companyPhone ||
          normalizedRecord.orgAdminProfile?.phone ||
          corporate?.orgAdminProfile?.phone ||
          null,
      });
    });

    if (!isSearching) {
      const fallbackId = normalizeString(initialValues?.corporateId);
      if (fallbackId && !map.has(fallbackId)) {
        map.set(fallbackId, {
          id: fallbackId,
          name:
            normalizeString(initialValues?.companyName) ||
            normalizeString(initialValues?.name) ||
            fallbackId,
          registrationNumber:
            normalizeString(initialValues?.companyRegistrationNumber) ||
            fallbackId.toUpperCase(),
          corporateRegistrationNumber:
            normalizeString(initialValues?.companyRegistrationNumber) ||
            normalizeString(initialValues?.companyRegNumber) ||
            fallbackId.toUpperCase(),
          organizationId:
            normalizeString(initialValues?.organizationId) || null,
          organizationName:
            normalizeString(initialValues?.companyName) ||
            normalizeString(initialValues?.organizationName) ||
            fallbackId,
          orgAdminProfile: initialValues?.orgAdminProfile || null,
          corporateProfile: initialValues?.orgAdminProfile || null,
          companyName:
            normalizeString(initialValues?.companyName) ||
            normalizeString(initialValues?.name) ||
            fallbackId,
          companyEmail: normalizeString(initialValues?.email) || null,
          companyPhone: normalizeString(initialValues?.phone) || null,
        });
      }
    }

    remoteCorporates.forEach((corporate) => {
      const normalizedId = normalizeString(corporate?.id);
      if (!normalizedId) {
        return;
      }

      if (mode === "create" && assignedCorporateIds.has(normalizedId)) {
        return;
      }

      const existing = map.get(normalizedId) || {};
      map.set(normalizedId, {
        ...existing,
        id: normalizedId,
        name: normalizeString(corporate.name) || existing.name || normalizedId,
        registrationNumber:
          normalizeString(corporate.registrationNumber) ||
          existing.registrationNumber ||
          normalizedId.toUpperCase(),
        corporateRegistrationNumber:
          normalizeString(corporate.corporateRegistrationNumber) ||
          normalizeString(corporate.registrationNumber) ||
          existing.corporateRegistrationNumber ||
          existing.registrationNumber ||
          normalizedId.toUpperCase(),
        organizationId:
          normalizeString(corporate.organizationId) ||
          existing.organizationId ||
          null,
        organizationName:
          normalizeString(corporate.organizationName) ||
          existing.organizationName ||
          normalizeString(corporate.name) ||
          existing.name ||
          normalizedId,
        orgAdminProfile:
          corporate.orgAdminProfile || existing.orgAdminProfile || null,
        corporateProfile:
          corporate.corporateProfile || existing.corporateProfile || null,
        companyName:
          normalizeString(corporate.companyName) ||
          normalizeString(corporate.name) ||
          existing.companyName ||
          existing.name ||
          normalizedId,
        companyEmail:
          corporate.companyEmail ||
          corporate.orgAdminProfile?.email ||
          existing.companyEmail ||
          existing.orgAdminProfile?.email ||
          null,
        companyPhone:
          corporate.companyPhone ||
          corporate.orgAdminProfile?.phone ||
          existing.companyPhone ||
          existing.orgAdminProfile?.phone ||
          null,
      });
    });

    return Array.from(map.values()).map((option) => ({
      ...option,
      isAssigned: mode === "create" && assignedCorporateIds.has(option.id),
    }));
  }, [
    mode,
    availableCorporates,
    corporates,
    initialValues,
    remoteCorporates,
    assignedCorporateIds,
    corporateQuery,
    isViewOnly,
  ]);

  useEffect(() => {
    if (isViewOnly) {
      return;
    }

    const pendingIds = corporateOptions
      .map((option) => {
        if (!option?.id) {
          return "";
        }

        const normalizedId = normalizeString(option.id);
        if (!normalizedId) {
          return "";
        }

        if (option.isAssigned || option.orgAdminProfile) {
          return "";
        }

        if (typeof assignableStatus[normalizedId] !== "undefined") {
          return "";
        }

        return normalizedId;
      })
      .filter(Boolean);

    const uniquePending = Array.from(new Set(pendingIds));
    if (!uniquePending.length) {
      return;
    }

    let isCancelled = false;

    const fetchStatuses = async () => {
      try {
        const responses = await Promise.allSettled(
          uniquePending.map((corporateId) =>
            orgAdminService.existsByCorporateId(corporateId)
          )
        );

        if (isCancelled) {
          return;
        }

        responses.forEach((result, index) => {
          const corporateId = uniquePending[index];
          if (result.status === "fulfilled") {
            const exists = resolveOrgAdminExistence(result.value);
            markAssignableStatus(corporateId, exists);
          } else {
            markAssignableStatus(corporateId, false);
          }
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }
      }
    };

    fetchStatuses();

    return () => {
      isCancelled = true;
    };
  }, [
    assignableStatus,
    corporateOptions,
    isViewOnly,
    markAssignableStatus,
    resolveOrgAdminExistence,
  ]);

  useEffect(() => {
    if (mode !== "create") {
      return;
    }

    setFormState((previous) => {
      if (previous.code) {
        return previous;
      }

      return {
        ...previous,
        code: generateNextOrgAdminCode(existingAssignments),
      };
    });
  }, [mode, existingAssignments]);

  useEffect(() => {
    setFormState((previous) => {
      const baseState = buildOrgAdminFormState(initialValues);
      if (mode === "create" && !baseState.code) {
        return {
          ...baseState,
          code: generateNextOrgAdminCode(existingAssignments),
        };
      }
      return baseState;
    });

    const nextCorporateId = normalizeString(initialValues?.corporateId);
    setSelectedCorporateId(nextCorporateId || "");

    if (nextCorporateId) {
      const corporate = corporates.find(
        (corp) => normalizeString(corp.id) === nextCorporateId
      );
      setCorporateQuery(
        corporate?.companyRegistrationNumber ||
          corporate?.registrationNumber ||
          normalizeString(initialValues?.companyRegistrationNumber) ||
          normalizeString(initialValues?.companyRegNumber) ||
          ""
      );
    } else {
      setCorporateQuery("");
    }
  }, [initialValues, corporates, mode, existingAssignments]);

  useEffect(() => {
    if (isViewOnly) {
      setRemoteCorporates([]);
      setCorporateSearchError(null);
      setIsCorporateSearchLoading(false);
      return;
    }

    const query = normalizeString(debouncedCorporateQuery);
    if (!query || query.length < CORPORATE_SEARCH_MIN_LENGTH) {
      setRemoteCorporates([]);
      setCorporateSearchError(null);
      setIsCorporateSearchLoading(false);
      return;
    }

    let isCancelled = false;
    setIsCorporateSearchLoading(true);
    setCorporateSearchError(null);

    const fetchCorporates = async () => {
      try {
        // Use the dedicated org admin search endpoint
        // This searches by both company name AND registration number
        // and only returns corporates that have associated ORG_ADMIN users
        const response = await complianceService.searchOrgAdmins({
          search: query,
          limit: CORPORATE_SEARCH_LIMIT,
        });

        if (isCancelled) {
          return;
        }

        const rawPayload = response?.data ?? response;
        const items = rawPayload?.items || [];

        // Normalize the results
        const normalized = items.map((item) => {
          const record = normalizeCorporateRecord(item);
          // Preserve org admin profile from the response
          if (item.orgAdminProfile) {
            record.orgAdminProfile = item.orgAdminProfile;
          }
          record.isOrgAdmin = true;
          return record;
        });

        setRemoteCorporates(normalized);
      } catch (error) {
        if (isCancelled) {
          return;
        }
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error.message ||
          "Unable to search organization administrators.";
        setCorporateSearchError(message);
        setRemoteCorporates([]);
      } finally {
        if (!isCancelled) {
          setIsCorporateSearchLoading(false);
        }
      }
    };

    fetchCorporates();

    return () => {
      isCancelled = true;
    };
  }, [debouncedCorporateQuery, isViewOnly]);

  useEffect(() => {
    const normalizedId = normalizeString(selectedCorporateId);
    if (!normalizedId) {
      setIsCorporateDetailsLoading(false);
      setCorporateSearchError(null);
      if (!isViewOnly) {
        setNotOrgAdminError("");
      }
      return;
    }

    // Validate that the ID is a valid MongoDB ObjectId format (24 hex characters)
    // This prevents 400 errors when non-ObjectId values like "corp-nbo" are used
    const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(normalizedId);
    if (!isValidObjectId) {
      setIsCorporateDetailsLoading(false);
      setCorporateSearchError(null);
      if (!isViewOnly) {
        setNotOrgAdminError("");
      }
      return;
    }

    let isActive = true;
    setIsCorporateDetailsLoading(true);
    setCorporateSearchError(null);

    const loadCorporateDetails = async () => {
      try {
        const response = await corporateDataService.get(normalizedId, {
          includeDetails: true,
          isDashboard: true,
        });

        if (!isActive) {
          return;
        }

        const baseRecord = response?.data?.corporate ?? response?.data ?? null;
        if (!baseRecord) {
          return;
        }

        let normalizedRecord = normalizeCorporateRecord(baseRecord);

        if (!normalizedRecord) {
          return;
        }

        const verificationOrganizationId =
          normalizeString(normalizedRecord.organizationId) || normalizedId;
        const verificationRegistrationNumber =
          sanitizeRegistrationNumberForVerification(
            normalizedRecord.corporateRegistrationNumber ||
              normalizedRecord.registrationNumber ||
              normalizedId
          );

        let verificationPayload = null;
        let orgAdminDetails = null;
        let hasOrgAdminFromService = false;

        if (verificationOrganizationId && verificationRegistrationNumber) {
          try {
            verificationPayload = await dispatch(
              verifyForOrganization({
                data: {
                  organizationId: verificationOrganizationId,
                  verificationKey: "companyRegNumber",
                  verificationValue: verificationRegistrationNumber,
                  idType: "Local Company",
                  for: "organization",
                },
              })
            ).unwrap();
          } catch (verificationError) {
            console.error(
              "Failed to verify corporate by registration number:",
              verificationError
            );
          }
        }

        try {
          // Use the compliance-specific endpoint for org admin lookup
          const orgAdminResponse =
            await complianceService.getOrgAdminByCorporate(normalizedId);
          const responseData = orgAdminResponse?.data ?? orgAdminResponse;
          hasOrgAdminFromService = responseData?.exists === true;

          if (hasOrgAdminFromService && responseData?.orgAdmin) {
            orgAdminDetails = responseData;
          }
        } catch (orgAdminResolveError) {
          if (orgAdminResolveError?.response?.status !== 404) {
            console.error(
              "[SuperComplianceDashboard] Failed to resolve org admin by corporate:",
              orgAdminResolveError
            );
          }
          hasOrgAdminFromService = false;
        }

        if (!isActive) {
          return;
        }

        if (verificationPayload?.corporate) {
          const mergedRecord = {
            ...baseRecord,
            ...verificationPayload.corporate,
            organization: {
              ...(baseRecord?.organization || {}),
              ...(verificationPayload.corporate?.organization || {}),
            },
          };
          normalizedRecord =
            normalizeCorporateRecord(mergedRecord) || normalizedRecord;
        }

        if (verificationPayload) {
          const verifiedOrgAdminProfile =
            buildContactProfile(verificationPayload.applicant) ||
            buildContactProfile(verificationPayload.registrar) ||
            buildContactProfile(verificationPayload.individual) ||
            buildContactProfile(verificationPayload.beneficialOwner) ||
            normalizedRecord.orgAdminProfile ||
            null;

          const corporateContactCandidate =
            verificationPayload.corporate?.contactPerson ||
            verificationPayload.corporate?.contactPersonDetails ||
            verificationPayload.corporate?.contactPersonInfo ||
            verificationPayload.corporate?.company ||
            verificationPayload.corporate;

          const verifiedCorporateProfile =
            buildContactProfile(corporateContactCandidate) ||
            normalizedRecord.corporateProfile ||
            null;

          const verifiedCompanyName = firstTruthyString(
            verificationPayload.corporate?.names?.companyName,
            verificationPayload.corporate?.organizationName,
            verificationPayload.corporate?.companyName,
            normalizedRecord.companyName
          );

          const verifiedRegistrationNumber =
            sanitizeRegistrationNumberForVerification(
              verificationPayload.corporate?.corporateRegistrationNumber ||
                verificationPayload.corporate?.registrationNumber
            ) || normalizedRecord.corporateRegistrationNumber;

          const verifiedEmail = firstTruthyString(
            verificationPayload.userEmail,
            verifiedCorporateProfile?.email,
            verifiedOrgAdminProfile?.email,
            normalizedRecord.companyEmail
          );

          const verifiedPhone = firstTruthyString(
            verifiedCorporateProfile?.phone,
            verifiedOrgAdminProfile?.phone,
            normalizedRecord.companyPhone
          );

          normalizedRecord = {
            ...normalizedRecord,
            ...(verifiedOrgAdminProfile
              ? { orgAdminProfile: verifiedOrgAdminProfile }
              : {}),
            ...(verifiedCorporateProfile
              ? { corporateProfile: verifiedCorporateProfile }
              : {}),
            ...(verifiedCompanyName
              ? {
                  name: verifiedCompanyName,
                  companyName: verifiedCompanyName,
                  organizationName:
                    firstTruthyString(
                      verificationPayload.corporate?.organizationName,
                      normalizedRecord.organizationName,
                      verifiedCompanyName
                    ) || verifiedCompanyName,
                }
              : {}),
            ...(verifiedRegistrationNumber
              ? {
                  registrationNumber: verifiedRegistrationNumber,
                  corporateRegistrationNumber: verifiedRegistrationNumber,
                }
              : {}),
            companyEmail: verifiedEmail || null,
            companyPhone: verifiedPhone || null,
            organizationId:
              firstTruthyString(
                verificationPayload.corporate?.organizationId,
                normalizedRecord.organizationId
              ) || normalizedRecord.organizationId,
          };
        }

        let serviceProfile = null;
        let serviceOrganizationId = "";
        let serviceCode = "";

        if (orgAdminDetails) {
          const serviceName = normalizeString(orgAdminDetails.name);
          const serviceEmail = normalizeString(orgAdminDetails.email);
          const servicePhone = normalizeString(orgAdminDetails.phone);
          serviceOrganizationId =
            normalizeString(orgAdminDetails.organizationId) || "";
          serviceCode = normalizeString(orgAdminDetails.code) || "";

          if (serviceName || serviceEmail || servicePhone) {
            serviceProfile = {
              name: serviceName || null,
              email: serviceEmail || null,
              phone: servicePhone || null,
            };
          }

          if (serviceOrganizationId) {
            normalizedRecord = {
              ...normalizedRecord,
              organizationId: serviceOrganizationId,
            };
          }

          if (serviceProfile) {
            normalizedRecord = {
              ...normalizedRecord,
              orgAdminProfile: {
                ...(normalizedRecord.orgAdminProfile || {}),
                ...serviceProfile,
              },
            };
          }
        }

        const hasOrgAdminFromVerification =
          isOrgAdminFromVerificationPayload(verificationPayload);
        const resolvedIsOrgAdmin =
          hasOrgAdminFromService || hasOrgAdminFromVerification;

        markAssignableStatus(normalizedId, resolvedIsOrgAdmin);

        if (!isViewOnly) {
          setNotOrgAdminError(
            resolvedIsOrgAdmin
              ? ""
              : "The entity exists in the system, but is not an OrgAdmin."
          );
        } else {
          setNotOrgAdminError("");
        }

        setRemoteCorporates((previous) => {
          const existingIndex = previous.findIndex(
            (item) => normalizeString(item?.id) === normalizedRecord.id
          );

          if (existingIndex === -1) {
            return [...previous, normalizedRecord];
          }

          const updated = [...previous];
          updated[existingIndex] = {
            ...updated[existingIndex],
            ...normalizedRecord,
          };
          return updated;
        });

        setFormState((previous) => {
          if (normalizeString(previous.corporateId) !== normalizedRecord.id) {
            return previous;
          }

          const resolvedCompanyName =
            normalizeString(normalizedRecord.companyName) ||
            normalizeString(normalizedRecord.name) ||
            previous.companyName;
          const resolvedRegistration =
            normalizeString(normalizedRecord.corporateRegistrationNumber) ||
            normalizeString(normalizedRecord.registrationNumber) ||
            previous.companyRegistrationNumber;
          const resolvedEmail =
            normalizeString(normalizedRecord.companyEmail) ||
            normalizeString(normalizedRecord.corporateProfile?.email) ||
            normalizeString(normalizedRecord.orgAdminProfile?.email) ||
            previous.email;
          const resolvedPhone =
            normalizeString(normalizedRecord.companyPhone) ||
            normalizeString(normalizedRecord.corporateProfile?.phone) ||
            normalizeString(normalizedRecord.orgAdminProfile?.phone) ||
            previous.phone;

          const maintainOrPopulate = (resolvedValue, existingValue) =>
            existingValue?.trim()
              ? existingValue
              : resolvedValue ?? existingValue ?? "";

          const nextState = {
            ...previous,
            organizationId:
              serviceOrganizationId ||
              normalizedRecord.organizationId ||
              previous.organizationId ||
              "",
            companyName: resolvedCompanyName,
            companyRegistrationNumber: resolvedRegistration,
            name: maintainOrPopulate(resolvedCompanyName, previous.name),
            email: maintainOrPopulate(resolvedEmail, previous.email),
            phone: maintainOrPopulate(resolvedPhone, previous.phone),
          };

          if (serviceCode) {
            nextState.code = serviceCode;
          }

          return nextState;
        });

        setCorporateSearchError(null);
      } catch (error) {
        if (!isActive) {
          return;
        }
        setCorporateSearchError(
          "Unable to load corporate details. Please try again."
        );
      } finally {
        if (isActive) {
          setIsCorporateDetailsLoading(false);
        }
      }
    };

    loadCorporateDetails();

    return () => {
      isActive = false;
    };
  }, [
    dispatch,
    isViewOnly,
    markAssignableStatus,
    resolveOrgAdminExistence,
    selectedCorporateId,
    setCorporateSearchError,
    setFormState,
    setIsCorporateDetailsLoading,
  ]);

  const filteredCorporateOptions = useMemo(() => {
    const query = corporateQuery.trim().toLowerCase();

    const matches = corporateOptions.filter((option) => {
      if (!option) {
        return false;
      }

      if (option.id === selectedCorporateId) {
        return true;
      }

      if (query) {
        const haystack = [
          option.name,
          option.registrationNumber,
          option.corporateRegistrationNumber,
          option.organizationName,
          option.companyName,
        ]
          .filter(Boolean)
          .map((item) => item.toLowerCase());

        const matchesQuery = haystack.some((value) => value.includes(query));
        if (!matchesQuery) {
          return false;
        }
      }

      return isOptionAssignable(option);
    });

    return matches.slice(0, DEFAULT_CORPORATE_RESULTS);
  }, [
    corporateOptions,
    corporateQuery,
    isOptionAssignable,
    selectedCorporateId,
  ]);

  const selectedCorporate = useMemo(
    () =>
      corporateOptions.find((option) => option.id === selectedCorporateId) ||
      null,
    [corporateOptions, selectedCorporateId]
  );

  const handleCorporateSelect = useCallback(
    (corporateId) => {
      if (isViewOnly) {
        return;
      }

      const normalizedId = normalizeString(corporateId);
      if (!normalizedId) {
        return;
      }

      if (mode === "create" && assignedCorporateIds.has(normalizedId)) {
        return;
      }

      const corporate =
        corporateOptions.find((option) => option.id === normalizedId) || null;

      if (!corporate) {
        return;
      }

      setSelectedCorporateId(normalizedId);
      setCorporateQuery(
        corporate.corporateRegistrationNumber ||
          corporate.registrationNumber ||
          ""
      );
      setShowCorporateOptions(false);
      setCorporateSearchError(null);
      setNotOrgAdminError("");

      setFormState((previous) => {
        const resolvedCompanyName =
          normalizeString(corporate.companyName) ||
          normalizeString(corporate.name) ||
          previous.companyName;
        const resolvedRegistration =
          normalizeString(corporate.corporateRegistrationNumber) ||
          normalizeString(corporate.registrationNumber) ||
          previous.companyRegistrationNumber;
        const resolvedEmail =
          normalizeString(corporate.companyEmail) ||
          normalizeString(corporate.corporateProfile?.email) ||
          normalizeString(corporate.orgAdminProfile?.email) ||
          previous.email;
        const resolvedPhone =
          normalizeString(corporate.companyPhone) ||
          normalizeString(corporate.corporateProfile?.phone) ||
          normalizeString(corporate.orgAdminProfile?.phone) ||
          previous.phone;

        const nextState = {
          ...previous,
          corporateId: normalizedId,
          organizationId:
            corporate.organizationId || previous.organizationId || "",
          companyName: resolvedCompanyName,
          companyRegistrationNumber: resolvedRegistration,
          name: resolvedCompanyName,
          email: resolvedEmail,
          phone: resolvedPhone,
        };

        if (mode === "create" && !previous.code) {
          nextState.code = generateNextOrgAdminCode(existingAssignments);
        }

        return nextState;
      });
    },
    [
      assignedCorporateIds,
      corporateOptions,
      existingAssignments,
      isViewOnly,
      mode,
    ]
  );

  const handleOrganizationChange = (event) => {
    const { value } = event.target;

    const existingCorporate =
      value && formState.corporateId
        ? corporateOptions.find(
            (option) =>
              option.id === formState.corporateId &&
              option.organizationId === value
          )
        : null;

    const fallbackCorporate =
      existingCorporate ||
      corporateOptions.find((option) => option.organizationId === value);

    setSelectedCorporateId(fallbackCorporate?.id || "");
    setCorporateQuery(
      fallbackCorporate?.corporateRegistrationNumber ||
        fallbackCorporate?.registrationNumber ||
        ""
    );

    setFormState((previous) => {
      let nextState = {
        ...previous,
        organizationId: value,
      };

      if (!value || !fallbackCorporate) {
        return {
          ...nextState,
          corporateId: "",
          companyName: "",
          companyRegistrationNumber: "",
          name: "",
          email: "",
          phone: "",
        };
      }

      const resolvedName =
        normalizeString(fallbackCorporate.companyName) ||
        normalizeString(fallbackCorporate.name) ||
        previous.companyName;
      const resolvedRegistration =
        normalizeString(fallbackCorporate.corporateRegistrationNumber) ||
        normalizeString(fallbackCorporate.registrationNumber) ||
        previous.companyRegistrationNumber;
      const resolvedEmail =
        normalizeString(fallbackCorporate.companyEmail) ||
        normalizeString(fallbackCorporate.corporateProfile?.email) ||
        normalizeString(fallbackCorporate.orgAdminProfile?.email) ||
        previous.email;
      const resolvedPhone =
        normalizeString(fallbackCorporate.companyPhone) ||
        normalizeString(fallbackCorporate.corporateProfile?.phone) ||
        normalizeString(fallbackCorporate.orgAdminProfile?.phone) ||
        previous.phone;

      nextState = {
        ...nextState,
        corporateId: fallbackCorporate.id,
        companyName: resolvedName,
        companyRegistrationNumber: resolvedRegistration,
        name: resolvedName,
        email: resolvedEmail,
        phone: resolvedPhone,
      };

      if (mode === "create" && !previous.code) {
        nextState.code = generateNextOrgAdminCode(existingAssignments);
      }

      return nextState;
    });
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;

    if (field === "organizationId") {
      const selected = organizations.find(
        (organization) => organization.id === value
      );

      const derivedName =
        selected?.name ||
        selected?.registrationName ||
        selected?.companyName ||
        selected?.businessName ||
        selected?.code ||
        value ||
        "";

      const derivedCode =
        selected?.code ||
        selected?.companyRegistrationNumber ||
        selected?.registrationNumber ||
        derivedName;

      const derivedCompanyName =
        selected?.companyName ||
        selected?.registrationName ||
        selected?.name ||
        derivedName;

      const derivedRegistration =
        selected?.companyRegistrationNumber ||
        selected?.registrationNumber ||
        derivedCode;

      const derivedEmail =
        selected?.contactPersonEmail || selected?.email || formState.email;

      const derivedPhone =
        selected?.contactPersonPhone || selected?.phone || formState.phone;

      setFormState((previous) => ({
        ...previous,
        organizationId: value,
        name: derivedName,
        code: derivedCode,
        companyName: derivedCompanyName,
        companyRegistrationNumber: derivedRegistration,
        email: derivedEmail,
        phone: derivedPhone,
      }));
      return;
    }

    setFormState((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isViewOnly) {
      onCancel();
      return;
    }

    if (notOrgAdminError) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        id: initialValues?.id || null,
        name: formState.name,
        code: formState.code,
        email: formState.email,
        phone: formState.phone,
        corporateId: formState.corporateId || null,
        organizationId: formState.organizationId || null,
        templateId: formState.templateId || null,
        companyName: formState.companyName,
        companyRegistrationNumber: formState.companyRegistrationNumber,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionLabel = mode === "edit" ? "Update Assignment" : "Assign Template";
  const showNoCorporateMatches =
    !isCorporateSearchLoading &&
    !corporateSearchError &&
    filteredCorporateOptions.length === 0;

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-xl border border-sflPrimary/20 bg-slate-50 p-4"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Corporate
          </label>
          <div className="relative mt-1">
            <SearchInput
              value={corporateQuery}
              handleOnChange={(event) => {
                if (isViewOnly) return;
                setCorporateQuery(event.target.value);
                setShowCorporateOptions(true);
              }}
              onFocus={() => {
                if (!isViewOnly) {
                  setShowCorporateOptions(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowCorporateOptions(false), 150);
              }}
              placeholder="e.g. BN/2007/245987"
              disabled={isViewOnly}
              className="max-w-none"
              autoComplete="off"
            />
            {showCorporateOptions && !isViewOnly && (
              <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                {isCorporateSearchLoading && (
                  <li className="px-3 py-2 text-sm text-gray-500">
                    Searching...
                  </li>
                )}
                {!isCorporateSearchLoading && corporateSearchError && (
                  <li className="px-3 py-2 text-sm text-red-500">
                    {corporateSearchError}
                  </li>
                )}
                {filteredCorporateOptions.map((option) => {
                  const isSelected = option.id === selectedCorporateId;
                  const isDisabled = option.isAssigned;
                  return (
                    <li key={option.id}>
                      <button
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleCorporateSelect(option.id)}
                        className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition ${
                          isSelected
                            ? "bg-sflPrimary/10 text-sflPrimary"
                            : isDisabled
                            ? "text-gray-400"
                            : "hover:bg-slate-100"
                        } ${isDisabled ? "cursor-not-allowed" : ""}`}
                        disabled={isDisabled}
                        aria-disabled={isDisabled}
                      >
                        <span className="font-medium">
                          {option.corporateRegistrationNumber ||
                            option.registrationNumber}
                        </span>
                        <span className="text-xs text-gray-500">
                          {option.companyName || option.name}
                        </span>
                        {isDisabled && (
                          <span className="text-[11px] font-medium text-rose-500">
                            Already assigned
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
                {showNoCorporateMatches && (
                  <li className="px-3 py-2 text-sm text-gray-500">
                    No corporates match your search.
                  </li>
                )}
              </ul>
            )}
          </div>
          {notOrgAdminError && (
            <p className="mt-2 text-sm text-red-600">{notOrgAdminError}</p>
          )}
          {selectedCorporate && (
            <div className="mt-2 space-y-1 text-xs text-gray-500">
              <p>
                Reg No:{" "}
                {selectedCorporate.corporateRegistrationNumber ||
                  selectedCorporate.registrationNumber}
              </p>
              {selectedCorporate.companyName && (
                <p>Company: {selectedCorporate.companyName}</p>
              )}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Org Admin Name
          </label>
          <input
            type="text"
            value={formState.name}
            onChange={handleChange("name")}
            className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 ${
              isViewOnly ? "bg-gray-100" : "bg-white"
            }`}
            placeholder="e.g. SFL Registrars"
            required={!isViewOnly}
            disabled={isViewOnly}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Org Admin Code
          </label>
          <input
            type="text"
            value={formState.code}
            readOnly
            className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 bg-gray-100"
            placeholder="e.g. ORG001"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Email
          </label>
          <input
            type="email"
            value={formState.email}
            onChange={handleChange("email")}
            className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 ${
              isViewOnly ? "bg-gray-100" : "bg-white"
            }`}
            placeholder="org-admin@sfl.co.ke"
            required={!isViewOnly}
            disabled={isViewOnly}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Phone
          </label>
          <input
            type="tel"
            value={formState.phone}
            onChange={handleChange("phone")}
            className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 ${
              isViewOnly ? "bg-gray-100" : "bg-white"
            }`}
            placeholder="e.g. +254700000000"
            required={!isViewOnly}
            disabled={isViewOnly}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Template
          </label>
          <select
            value={formState.templateId}
            onChange={handleChange("templateId")}
            className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
            disabled={isViewOnly}
            required={!isViewOnly}
          >
            <option value="">Select a template</option>
            {/* 
                Show blueprints (SUPER_ADMIN level) by default. 
                If the currently assigned template is an ORG_ADMIN copy, 
                include it so it's visible in the dropdown.
            */}
            {(() => {
              const currentId = formState.templateId;
              const options = [...templates];

              if (currentId && !options.some((t) => t.id === currentId)) {
                const currentTemplate = allTemplates.find(
                  (t) => t.id === currentId
                );
                if (currentTemplate) {
                  options.push(currentTemplate);
                }
              }

              return options.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                  {template.version ? ` (v${template.version})` : ""}
                </option>
              ));
            })()}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2"
        >
          {isViewOnly ? "Close" : "Cancel"}
        </button>
        {!isViewOnly && (
          <button
            type="submit"
            disabled={
              isSubmitting ||
              isCorporateDetailsLoading ||
              !formState.corporateId ||
              Boolean(notOrgAdminError)
            }
            className="inline-flex items-center justify-center rounded-md bg-sflPrimary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-sflPrimary/40"
          >
            {isSubmitting
              ? "Saving..."
              : isCorporateDetailsLoading
              ? "Loading corporate..."
              : actionLabel}
          </button>
        )}
      </div>
    </form>
  );
};

OrgAdminAssignmentForm.propTypes = {
  mode: PropTypes.oneOf(["create", "edit", "view"]).isRequired,
  initialValues: PropTypes.object,
  templates: PropTypes.array.isRequired,
  allTemplates: PropTypes.array.isRequired,
  availableCorporates: PropTypes.array,
  existingAssignments: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

OrgAdminAssignmentForm.defaultProps = {
  initialValues: null,
  availableCorporates: [],
};

const OrgAdminAssignmentsSection = ({
  orgAdmins,
  allTemplates,
  corporates,
  onSave,
  onDelete,
  isLoading,
}) => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [formConfig, setFormConfig] = useState(null);
  const { modalRow } = useSelector((state) => state.modalsSlice);

  const assignedCorporateIds = useMemo(() => {
    const ids = new Set();
    (orgAdmins || []).forEach((admin) => {
      if (admin.corporateId) {
        ids.add(admin.corporateId);
      }
    });
    return ids;
  }, [orgAdmins]);

  const availableCorporatesForCreate = useMemo(
    () =>
      (corporates || []).filter(
        (corporate) => !assignedCorporateIds.has(corporate.id)
      ),
    [corporates, assignedCorporateIds]
  );

  const openForm = useCallback((mode, admin = null) => {
    setFormConfig({ mode, admin });
  }, []);

  const handleCloseForm = () => {
    if (!isMountedRef.current) return;
    setFormConfig(null);
  };

  const handleSubmit = async (payload) => {
    await onSave(payload);
    handleCloseForm();
  };

  const handleDelete = async () => {
    if (!modalRow?.id) return;
    await onDelete(modalRow.id);
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">
            Assign Templates to Org Admins
          </h4>
          <p className="text-sm text-gray-600">
            Create and manage template assignments for corporate administrators.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openForm("create")}
          className="inline-flex items-center justify-center rounded-md bg-sflPrimary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-sflPrimary/40"
        >
          + Add Org Admin
        </button>
      </div>

      {formConfig && (
        <OrgAdminAssignmentForm
          mode={formConfig.mode}
          initialValues={formConfig.admin}
          templates={allTemplates}
          corporates={corporates}
          availableCorporates={
            formConfig.mode === "create"
              ? availableCorporatesForCreate
              : corporates
          }
          existingAssignments={orgAdmins}
          onSubmit={handleSubmit}
          onCancel={handleCloseForm}
        />
      )}
      <OrgAdminAssignmentsTable
        assignments={orgAdmins}
        allTemplates={allTemplates}
        corporates={corporates}
        isLoading={isLoading}
        onView={(admin) => openForm("view", admin)}
        onEdit={(admin) => openForm("edit", admin)}
      />

      <ConfirmDialog
        name={DELETE_ORG_ADMIN_ASSIGNMENT_MODAL}
        title="Remove Org Admin Assignment"
        loadingText="Deleting..."
        onConfirm={handleDelete}
      >
        Are you sure you want to permanently delete this Org Admin assignment?
      </ConfirmDialog>
    </section>
  );
};

OrgAdminAssignmentsSection.propTypes = {
  orgAdmins: PropTypes.array.isRequired,
  allTemplates: PropTypes.array.isRequired,
  corporates: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

OrgAdminAssignmentsSection.defaultProps = {
  isLoading: false,
  allTemplates: [],
};
const TemplateEditor = ({
  template,
  frequencyOptions,
  timelineTypes,
  onUpdateCategory,
  onAddCategory,
  onRemoveCategory,
  onUpdateTask,
  onAddTask,
  onRemoveTask,
}) => {
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [hasManualCategorySelection, setHasManualCategorySelection] =
    useState(false);
  const [expandedTask, setExpandedTask] = useState(null);

  React.useEffect(() => {
    if (!template.categories?.length) {
      setExpandedCategoryId(null);
      setHasManualCategorySelection(false);
      return;
    }
    const categories = template.categories;
    const categoryExists =
      expandedCategoryId &&
      categories.some((category) => category.id === expandedCategoryId);

    if (expandedCategoryId && !categoryExists) {
      setExpandedCategoryId(null);
      setHasManualCategorySelection(false);
      return;
    }
  }, [template, expandedCategoryId, hasManualCategorySelection]);

  React.useEffect(() => {
    if (!expandedTask) {
      return;
    }

    const normalizedCategoryId = normalizeString(expandedTask.categoryId);
    const normalizedTaskId = normalizeString(expandedTask.taskId);

    if (!normalizedCategoryId || !normalizedTaskId) {
      setExpandedTask(null);
      return;
    }

    const category = template.categories.find(
      (candidate) => normalizeString(candidate.id) === normalizedCategoryId
    );

    if (!category) {
      setExpandedTask(null);
      return;
    }

    const taskExists = (category.tasks || []).some(
      (task) =>
        task.lifecycleStatus !== "delete" &&
        normalizeString(task.id) === normalizedTaskId
    );

    if (!taskExists) {
      setExpandedTask(null);
    }
  }, [template, expandedTask]);

  React.useEffect(() => {
    setExpandedTask(null);
  }, [expandedCategoryId]);

  const timelineOptions = [
    { value: timelineTypes.DEFINITE, label: "Definite" },
    { value: timelineTypes.INDEFINITE, label: "Indefinite" },
  ];

  const handleAddCategory = () => {
    const newCategoryId = onAddCategory(template.id);
    if (newCategoryId) {
      setExpandedCategoryId(newCategoryId);
      setHasManualCategorySelection(true);
    }
  };

  const handleToggleTask = useCallback((categoryId, taskId) => {
    const normalizedCategoryId = normalizeString(categoryId);
    const normalizedTaskId = normalizeString(taskId);

    if (!normalizedCategoryId || !normalizedTaskId) {
      return;
    }

    setExpandedTask((previous) => {
      if (
        previous &&
        previous.categoryId === normalizedCategoryId &&
        previous.taskId === normalizedTaskId
      ) {
        return null;
      }

      return { categoryId: normalizedCategoryId, taskId: normalizedTaskId };
    });
  }, []);

  const handleAddTask = useCallback(
    (categoryId) => {
      onAddTask(template.id, categoryId, {
        title: "New Compliance Task",
      });
    },
    [onAddTask, template.id]
  );

  return (
    <section className="space-y-5">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Update categories and underlying tasks before assigning to
            corporates.
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddCategory}
          className="inline-flex items-center justify-center rounded-md border border-sflPrimary px-4 py-2 text-sm font-semibold text-sflPrimary transition hover:bg-sflPrimary/10 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2"
        >
          + Add Category
        </button>
      </header>

      <div className="space-y-4">
        {template.categories.map((category) => {
          const isOpen = expandedCategoryId === category.id;
          const visibleTasks = (category.tasks || []).filter(
            (task) => task.lifecycleStatus !== "delete"
          );
          const normalizedCategoryId = normalizeString(category.id);

          return (
            <div
              key={category.id}
              className="rounded-2xl border border-gray-200"
            >
              <button
                type="button"
                onClick={() => {
                  setHasManualCategorySelection(true);
                  setExpandedCategoryId(isOpen ? null : category.id);
                }}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
              >
                <div>
                  <p className="text-base font-semibold text-gray-800">
                    {category.name}
                  </p>
                  {category.description && (
                    <p className="text-sm text-gray-600">
                      {category.description}
                    </p>
                  )}
                </div>
                <div className="text-xs font-semibold text-gray-500">
                  {visibleTasks.length} task
                  {visibleTasks.length === 1 ? "" : "s"}
                </div>
              </button>
              {isOpen && (
                <div className="space-y-6 border-t border-gray-200 p-5">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Category Name
                      </label>
                      <input
                        type="text"
                        value={category.name}
                        onChange={(event) =>
                          onUpdateCategory(template.id, category.id, {
                            name: event.target.value,
                          })
                        }
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Weight (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={category.weight ?? 0}
                        onChange={(event) =>
                          onUpdateCategory(template.id, category.id, {
                            weight: event.target.value,
                          })
                        }
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Description
                      </label>
                      <textarea
                        value={category.description || ""}
                        onChange={(event) =>
                          onUpdateCategory(template.id, category.id, {
                            description: event.target.value,
                          })
                        }
                        rows={3}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    {visibleTasks.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                        No tasks configured yet.
                      </div>
                    ) : (
                      visibleTasks.map((task) => {
                        const normalizedTaskId = normalizeString(task.id);
                        const isTaskOpen =
                          expandedTask?.categoryId === normalizedCategoryId &&
                          expandedTask?.taskId === normalizedTaskId;
                        const timelineType =
                          task.timelineType || timelineTypes.DEFINITE;
                        const timelineLabel =
                          timelineOptions.find(
                            (option) => option.value === timelineType
                          )?.label || "Definite";
                        const frequencyLabel = frequencyOptions.find(
                          (option) => option.value === task.frequency
                        )?.label;
                        const headerMetaParts = [`${timelineLabel} timeline`];

                        if (timelineType === timelineTypes.INDEFINITE) {
                          if (frequencyLabel) {
                            headerMetaParts.push(
                              `Repeats ${frequencyLabel.toLowerCase()}`
                            );
                          }
                        } else {
                          headerMetaParts.push(
                            task.deadline
                              ? `Due ${task.deadline}`
                              : "No deadline set"
                          );
                        }

                        if (task.startDate) {
                          headerMetaParts.push(`Starts ${task.startDate}`);
                        }

                        const headerMeta = headerMetaParts.join(" • ");

                        return (
                          <div
                            key={task.id}
                            className="rounded-xl border border-gray-200 p-4 shadow-sm"
                          >
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleTask(category.id, task.id)
                                }
                                className="flex flex-1 items-center justify-between gap-3 text-left"
                                aria-expanded={isTaskOpen}
                              >
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold text-gray-800">
                                      {task.title || "Untitled Task"}
                                    </p>
                                    {task.isMandatory && (
                                      <span className="inline-flex items-center gap-1 rounded-full border border-sflPrimary/40 bg-sflPrimary/10 px-2.5 py-0.5 text-[0.6875rem] font-semibold text-sflPrimary">
                                        <LockClosedIcon
                                          className="h-3.5 w-3.5"
                                          aria-hidden="true"
                                        />
                                        <span>Mandatory</span>
                                      </span>
                                    )}
                                  </div>
                                  {headerMeta && (
                                    <p className="mt-1 text-xs text-gray-500">
                                      {headerMeta}
                                    </p>
                                  )}
                                </div>
                                <span
                                  className={`text-xs font-semibold ${
                                    isTaskOpen
                                      ? "text-sflPrimary"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {isTaskOpen ? "Hide details" : "View details"}
                                </span>
                              </button>
                            </div>

                            {isTaskOpen && (
                              <div className="mt-4 space-y-4">
                                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                  <div className="flex-1">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Task Title
                                    </label>
                                    <input
                                      type="text"
                                      value={task.title}
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            title: event.target.value,
                                          }
                                        )
                                      }
                                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                                    />
                                  </div>
                                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 md:self-center md:pt-6">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(task.isMandatory)}
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            isMandatory: event.target.checked,
                                            mandatoryBy: event.target.checked
                                              ? "superAdmin"
                                              : null,
                                          }
                                        )
                                      }
                                      className="h-4 w-4 rounded border-gray-300 text-sflPrimary focus:ring-sflPrimary"
                                    />
                                    <span>Mandatory (lock downstream)</span>
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      onRemoveTask(
                                        template.id,
                                        category.id,
                                        task.id
                                      )
                                    }
                                    className="inline-flex items-center justify-center rounded-md border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-1"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div className="grid gap-4 md:grid-cols-2">
                                  <div className="md:col-span-2">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Description
                                    </label>
                                    <textarea
                                      value={task.description || ""}
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            description: event.target.value,
                                          }
                                        )
                                      }
                                      rows={3}
                                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Timeline
                                    </label>
                                    <select
                                      value={
                                        task.timelineType ||
                                        timelineTypes.DEFINITE
                                      }
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            timelineType: event.target.value,
                                          }
                                        )
                                      }
                                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                                    >
                                      {timelineOptions.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Checklist Header
                                    </label>
                                    <input
                                      type="text"
                                      value={task.checklistHeader || ""}
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            checklistHeader: event.target.value,
                                          }
                                        )
                                      }
                                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                                    />
                                  </div>
                                  {/* Scheduling fields hidden in Super Admin template editor */}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="flex justify-between items-center">
                    <button
                      type="button"
                      onClick={() => onRemoveCategory(template.id, category.id)}
                      className="inline-flex items-center justify-center rounded-md border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      Remove Category
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddTask(category.id)}
                      className="inline-flex items-center justify-center rounded-md border border-sflPrimary px-3 py-1 text-xs font-semibold text-sflPrimary transition hover:bg-sflPrimary/10 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2"
                    >
                      + Add Task
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

TemplateEditor.propTypes = {
  template: PropTypes.object.isRequired,
  frequencyOptions: PropTypes.array.isRequired,
  timelineTypes: PropTypes.object.isRequired,
  onUpdateCategory: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func.isRequired,
  onRemoveCategory: PropTypes.func.isRequired,
  onUpdateTask: PropTypes.func.isRequired,
  onAddTask: PropTypes.func.isRequired,
  onRemoveTask: PropTypes.func.isRequired,
};

const SettingsView = ({
  templates,
  allTemplates,
  templateDraft,
  onDraftChange,
  onTemplateSubmit,
  onStartCreate,
  onCancelCreate,
  isTemplateFormOpen,
  selectedTemplateId,
  onViewTemplate,
  onEditTemplate,
  onCloseTemplate,
  onDeleteTemplate,
  selectedTemplate,
  templatePanelMode,
  orgAdmins,
  isLoadingCompliance,
  isComplianceInitialized,
  corporates,
  onSaveOrgAdmin,
  onDeleteOrgAdmin,
  frequencyOptions,
  timelineTypes,
  onUpdateCategory,
  onAddCategory,
  onRemoveCategory,
  onUpdateTask,
  onAddTask,
  superAdminIdentity,
  superSummary,
  onRemoveTask,
}) => {
  const { modalRow } = useSelector((state) => state.modalsSlice);

  const handleConfirmDeleteTemplate = useCallback(() => {
    if (!modalRow?.id) return;
    onDeleteTemplate(modalRow.id);
  }, [modalRow, onDeleteTemplate]);

  const isViewingTemplate =
    Boolean(selectedTemplate) && templatePanelMode === "view";
  const isEditingTemplate =
    Boolean(selectedTemplate) && templatePanelMode === "edit";

  return (
    <section className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <header className="flex flex-col gap-2 border-b border-gray-200 pb-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            Compliance Template Management
          </h3>
          <p className="text-sm text-gray-600">
            Create templates and assign them to organization administrators as
            annual compliance defaults.
          </p>
        </div>
        <button
          type="button"
          onClick={onStartCreate}
          className="inline-flex items-center justify-center rounded-md bg-sflPrimary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2"
        >
          + Add a New Template
        </button>
      </header>

      {isTemplateFormOpen && (
        <TemplateForm
          templateDraft={templateDraft}
          onChange={onDraftChange}
          onSubmit={onTemplateSubmit}
          onCancel={onCancelCreate}
        />
      )}

      <TemplatesTable
        templates={templates}
        selectedTemplateId={selectedTemplateId}
        onView={onViewTemplate}
        onEdit={onEditTemplate}
        onClose={onCloseTemplate}
        templatePanelMode={templatePanelMode}
        selectedTemplate={selectedTemplate}
        frequencyOptions={frequencyOptions}
        timelineTypes={timelineTypes}
        onUpdateCategory={onUpdateCategory}
        onAddCategory={onAddCategory}
        onRemoveCategory={onRemoveCategory}
        onUpdateTask={onUpdateTask}
        onAddTask={onAddTask}
        onRemoveTask={onRemoveTask}
        TemplateEditor={TemplateEditor}
      />

      <OrgAdminAssignmentsSection
        orgAdmins={orgAdmins}
        allTemplates={allTemplates}
        corporates={corporates}
        onSave={onSaveOrgAdmin}
        onDelete={onDeleteOrgAdmin}
        isLoading={isLoadingCompliance && !isComplianceInitialized}
      />

      {/* Sanctions List Management - Super Admin Only */}
      <div className="mt-8 border-t border-gray-200 pt-8">
        <SanctionListManager
          isSuperAdmin={superAdminIdentity}
          isViewOnly={false}
        />
      </div>

      {/* Sanctions List Cron Job Management - Super Admin Only */}
      <div className="mt-8 border-t border-gray-200 pt-8">
        <SanctionListCronManager />
      </div>

      <ConfirmDialog
        name={DELETE_TEMPLATE_MODAL}
        title="Delete Compliance Template"
        loadingText="Deleting..."
        onConfirm={handleConfirmDeleteTemplate}
      >
        Are you sure you want to archive this compliance template?
      </ConfirmDialog>
    </section>
  );
};

SettingsView.propTypes = {
  templates: PropTypes.array.isRequired,
  allTemplates: PropTypes.array.isRequired,
  templateDraft: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onDraftChange: PropTypes.func.isRequired,
  onTemplateSubmit: PropTypes.func.isRequired,
  onStartCreate: PropTypes.func.isRequired,
  onCancelCreate: PropTypes.func.isRequired,
  isTemplateFormOpen: PropTypes.bool.isRequired,
  selectedTemplateId: PropTypes.string,
  onViewTemplate: PropTypes.func.isRequired,
  onEditTemplate: PropTypes.func.isRequired,
  onCloseTemplate: PropTypes.func.isRequired,
  onDeleteTemplate: PropTypes.func.isRequired,
  selectedTemplate: PropTypes.object,
  templatePanelMode: PropTypes.oneOf(["view", "edit"]).isRequired,
  orgAdmins: PropTypes.array.isRequired,
  corporates: PropTypes.array.isRequired,
  onSaveOrgAdmin: PropTypes.func.isRequired,
  onDeleteOrgAdmin: PropTypes.func.isRequired,
  frequencyOptions: PropTypes.array.isRequired,
  timelineTypes: PropTypes.object.isRequired,
  onUpdateCategory: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func.isRequired,
  onRemoveCategory: PropTypes.func.isRequired,
  onUpdateTask: PropTypes.func.isRequired,
  onAddTask: PropTypes.func.isRequired,
  onRemoveTask: PropTypes.func.isRequired,
  isLoadingCompliance: PropTypes.bool,
  isComplianceInitialized: PropTypes.bool,
  superAdminIdentity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    code: PropTypes.string,
    email: PropTypes.string,
    roleDescriptor: PropTypes.string,
    complianceRoles: PropTypes.arrayOf(PropTypes.string),
    hasRegistrarRole: PropTypes.bool,
    hasUploaderRole: PropTypes.bool,
    isOrgOrSuperAdmin: PropTypes.bool,
    corporateIds: PropTypes.array,
  }),
  superSummary: PropTypes.object,
};

SettingsView.defaultProps = {
  selectedTemplateId: null,
  selectedTemplate: null,
  templatePanelMode: "edit",
  isLoadingCompliance: false,
  isComplianceInitialized: false,
  superAdminIdentity: null,
  superSummary: null,
};
const SuperComplianceDashboard = ({ activeView }) => {
  const {
    superSummary,
    templates: allTemplates,
    orgAdmins,
    corporates,
    saveOrganizationAdmin,
    revokeTemplateAssignment,
    createTemplate,
    deleteTemplate,
    addTemplateCategory,
    updateTemplateCategory,
    removeTemplateCategory,
    addTemplateTask,
    updateTemplateTask,
    removeTemplateTask,
    assignTemplateToCorporate,
    corporateSummaries,
    companySummaries,
    listCorporateTasks,
    listCompanyTasks,
    superAdminProfile,
    FREQUENCY_OPTIONS,
    TIMELINE_TYPES,
    // Backend integration states
    isLoading: isLoadingCompliance,
    error: complianceError,
    isInitialized: isComplianceInitialized,
    refetchComplianceData,
  } = useComplianceContext();

  // Filter out organizational copies (ORG_ADMIN level) from the Super Admin view
  const templates = useMemo(
    () => (allTemplates || []).filter((t) => t.level === "SUPER_ADMIN"),
    [allTemplates]
  );

  // Ensure compliance data is fetched when this component mounts
  // This handles cases where the component is rendered on non-compliance routes
  useEffect(() => {
    if (!isComplianceInitialized && !isLoadingCompliance) {
      refetchComplianceData();
    }
  }, [isComplianceInitialized, isLoadingCompliance, refetchComplianceData]);

  const {
    aggregatedRoles,
    hasRegistrarRole,
    hasUploaderRole,
    isOrgOrSuperAdmin,
  } = useComplianceAccess();
  const currentUser = useSelector(
    (state) => state.authSlice?.currentUser ?? null
  );
  const dispatch = useDispatch();

  const {
    identity: superAdminIdentity,
    summary: resolvedSuperSummary,
    profile: resolvedSuperAdminProfile,
  } = useMemo(
    () =>
      buildSuperAdminDetails({
        currentUser,
        fallbackSummary: superSummary,
        fallbackProfile: superAdminProfile,
        aggregatedRoles,
        hasRegistrarRole,
        hasUploaderRole,
        isOrgOrSuperAdmin,
      }),
    [
      aggregatedRoles,
      currentUser,
      hasRegistrarRole,
      hasUploaderRole,
      isOrgOrSuperAdmin,
      superAdminProfile,
      superSummary,
    ]
  );

  const superAdminDisplayName =
    superAdminIdentity.name || DEFAULT_SUPER_ADMIN_NAME;

  const [templateDraft, setTemplateDraft] = useState(createEmptyTemplateDraft);
  const [isTemplateFormOpen, setIsTemplateFormOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templatePanelMode, setTemplatePanelMode] = useState("edit");
  const [hasManualTemplateSelection, setHasManualTemplateSelection] =
    useState(false);

  const resetTemplateDraft = React.useCallback(() => {
    setTemplateDraft(createEmptyTemplateDraft());
  }, [setTemplateDraft]);

  const normalizedView = useMemo(
    () => (ALLOWED_VIEWS.has(activeView) ? activeView : "summary"),
    [activeView]
  );

  const trackerEntities = useMemo(() => {
    const corporateIds = (orgAdmins || [])
      .map((admin) => admin.corporateId)
      .filter(Boolean);

    if (!corporateIds.length) {
      return [];
    }

    const syntheticProfile = {
      corporateIds,
      companyIds: [],
    };

    const orgAdminsByCorporateId = new Map(
      (orgAdmins || [])
        .filter((admin) => admin.corporateId)
        .map((admin) => [String(admin.corporateId), admin])
    );

    const baseEntities = buildTrackerEntities(
      syntheticProfile,
      corporateSummaries,
      companySummaries,
      listCorporateTasks,
      listCompanyTasks
    );

    return baseEntities.map((entity) => {
      if (!entity?.id) return entity;
      const orgAdmin = orgAdminsByCorporateId.get(String(entity.id));
      const resolvedName =
        orgAdmin?.companyName || orgAdmin?.name || entity.name;
      return {
        ...entity,
        name: resolvedName,
      };
    });
  }, [
    orgAdmins,
    corporateSummaries,
    companySummaries,
    listCorporateTasks,
    listCompanyTasks,
  ]);

  const assignedAdmins = useMemo(
    () => (orgAdmins || []).filter((admin) => admin?.templateId),
    [orgAdmins]
  );

  const metricsProfile = useMemo(() => {
    const corporateIds = (assignedAdmins || [])
      .map((admin) => admin.corporateId)
      .filter(Boolean);

    const collectedTasks = [];
    corporateIds.forEach((corporateId) => {
      const corpTasks = listCorporateTasks(corporateId) || [];
      collectedTasks.push(...corpTasks);
      const corporate =
        (corporates || []).find(
          (c) => String(c?.id || c?._id) === String(corporateId)
        ) || null;
      if (corporate) {
        const assocIds =
          Array.isArray(corporate.associatedCompanyIds) &&
          corporate.associatedCompanyIds.length > 0
            ? corporate.associatedCompanyIds
            : Array.isArray(corporate.associatedCompanies)
            ? corporate.associatedCompanies
                .map((co) => co?._id || co?.id)
                .filter(Boolean)
            : [];
        assocIds.forEach((companyId) => {
          const companyTasks = listCompanyTasks(companyId) || [];
          collectedTasks.push(...companyTasks);
        });
      }
    });

    const hasOrgAdminScope = corporateIds.length > 0;
    const tasks = hasOrgAdminScope
      ? collectedTasks
      : resolvedSuperSummary?.tasks || [];

    const firstAdminWithCorporate = (assignedAdmins || []).find(
      (admin) => admin.corporateId
    );

    const resolvedName =
      superAdminIdentity?.companyName ||
      superAdminProfile?.companyName ||
      firstAdminWithCorporate?.companyName ||
      superAdminIdentity.name ||
      resolvedSuperSummary?.name ||
      DEFAULT_SUPER_ADMIN_NAME;

    const resolvedCode =
      firstAdminWithCorporate?.code ||
      resolvedSuperSummary?.code ||
      resolvedSuperAdminProfile?.code ||
      superAdminIdentity.code ||
      DEFAULT_SUPER_ADMIN_CODE;

    return {
      code: resolvedCode,
      name: resolvedName,
      roleDescriptor: superAdminIdentity.roleDescriptor,
      complianceRoles: superAdminIdentity.complianceRoles,
      tasks,
    };
  }, [
    assignedAdmins,
    listCorporateTasks,
    resolvedSuperSummary,
    resolvedSuperAdminProfile,
    superAdminIdentity,
  ]);

  const selectedTemplate = useMemo(
    () =>
      templates.find((template) => template.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  React.useEffect(() => {
    if (!templates.length) {
      setSelectedTemplateId(null);
      setTemplatePanelMode("edit");
      setHasManualTemplateSelection(false);
      return;
    }

    const selectedExists =
      selectedTemplateId &&
      templates.some((template) => template.id === selectedTemplateId);

    if (selectedTemplateId && !selectedExists) {
      setSelectedTemplateId(null);
      setTemplatePanelMode("edit");
      setHasManualTemplateSelection(false);
    }
  }, [templates, selectedTemplateId, hasManualTemplateSelection]);

  const handleStartCreate = () => {
    resetTemplateDraft();
    setIsTemplateFormOpen(true);
  };

  const handleCancelCreate = () => {
    resetTemplateDraft();
    setIsTemplateFormOpen(false);
  };

  const handleTemplateSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = templateDraft.name.trim();
    if (!trimmedName) return;

    const newTemplateId = await createTemplate({
      name: trimmedName,
      description: templateDraft.description.trim(),
      ownerType: "superAdmin",
      createdBy: superAdminDisplayName,
    });

    resetTemplateDraft();
    setIsTemplateFormOpen(false);
    if (newTemplateId) {
      setSelectedTemplateId(newTemplateId);
      setTemplatePanelMode("edit");
      setHasManualTemplateSelection(true);
    }
  };

  const handleViewTemplate = (templateId) => {
    if (!templateId) return;
    setSelectedTemplateId(templateId);
    setTemplatePanelMode("view");
    setIsTemplateFormOpen(false);
    setHasManualTemplateSelection(true);
  };

  const handleEditTemplate = (templateId) => {
    if (!templateId) return;
    setSelectedTemplateId(templateId);
    setTemplatePanelMode("edit");
    setIsTemplateFormOpen(false);
    setHasManualTemplateSelection(true);
  };

  const handleCloseTemplate = () => {
    setSelectedTemplateId(null);
    setTemplatePanelMode("edit");
    setHasManualTemplateSelection(true);
  };

  const handleDeleteTemplate = (templateId) => {
    if (!templateId) return;
    deleteTemplate(templateId);
    if (selectedTemplateId === templateId) {
      setSelectedTemplateId(null);
      setTemplatePanelMode("edit");
      setHasManualTemplateSelection(false);
    }
  };

  const handleCategoryUpdate = (templateId, categoryId, updates) => {
    updateTemplateCategory(templateId, categoryId, updates, {
      updatedBy: superAdminDisplayName,
    });
  };

  const handleAddCategory = (templateId) =>
    addTemplateCategory(templateId, {}, { updatedBy: superAdminDisplayName });

  const handleRemoveCategory = (templateId, categoryId) => {
    removeTemplateCategory(templateId, categoryId, {
      updatedBy: superAdminDisplayName,
    });
  };

  const handleAddTask = (templateId, categoryId, taskPayload) =>
    addTemplateTask(templateId, categoryId, taskPayload, {
      updatedBy: superAdminDisplayName,
    });

  const handleUpdateTask = (templateId, categoryId, taskId, updates) => {
    updateTemplateTask(templateId, categoryId, taskId, updates, {
      updatedBy: superAdminDisplayName,
    });
  };

  const handleRemoveTask = (templateId, categoryId, taskId) => {
    removeTemplateTask(templateId, categoryId, taskId, {
      updatedBy: superAdminDisplayName,
    });
  };

  const normalizedSummary = resolvedSuperSummary?.summary || {};

  const handleSaveOrgAdmin = (payload) => {
    if (!payload) return;

    saveOrganizationAdmin({
      ...payload,
      templateId: payload.templateId || null,
    });

    if (payload.corporateId && payload.templateId) {
      assignTemplateToCorporate(payload.corporateId, payload.templateId, {
        appliedBy: superAdminDisplayName,
        origin: "superAdmin",
        code: payload.code,
      });
    }
  };

  const handleDeleteOrgAdmin = async (adminAssignmentId) => {
    if (!adminAssignmentId) return;
    await revokeTemplateAssignment(adminAssignmentId);
  };

  return (
    <div className="space-y-8">
      {isLoadingCompliance && !isComplianceInitialized && (
        <div className="animate-pulse rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
            <p className="text-sm font-medium text-blue-700">
              Loading compliance data from server...
            </p>
          </div>
        </div>
      )}

      {complianceError && !isLoadingCompliance && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex items-start gap-3">
            <svg
              className="h-5 w-5 flex-shrink-0 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">
                Unable to connect to backend
              </p>
              <p className="mt-1 text-xs text-yellow-700">
                Using local data. Changes will not be saved. Error:{" "}
                {complianceError}
              </p>
            </div>
          </div>
        </div>
      )}

      {normalizedView === "summary" && (
        <SummaryView
          displayName={metricsProfile?.name}
          orgAdminCount={(assignedAdmins || []).length}
          tasks={metricsProfile?.tasks}
        />
      )}

      {normalizedView === "metrics" && (
        <MetricsView
          assignedAdmins={assignedAdmins}
          corporates={corporates}
          listCorporateTasks={listCorporateTasks}
          listCompanyTasks={listCompanyTasks}
        />
      )}

      {normalizedView === "tracker" && (
        <TrackerView
          trackerEntities={trackerEntities}
          superAdminName={superAdminIdentity.name}
        />
      )}

      {normalizedView === "settings" && (
        <SettingsView
          templates={templates}
          allTemplates={allTemplates}
          templateDraft={templateDraft}
          onDraftChange={setTemplateDraft}
          onTemplateSubmit={handleTemplateSubmit}
          onStartCreate={handleStartCreate}
          onCancelCreate={handleCancelCreate}
          isTemplateFormOpen={isTemplateFormOpen}
          selectedTemplateId={selectedTemplateId}
          onViewTemplate={handleViewTemplate}
          onEditTemplate={handleEditTemplate}
          onCloseTemplate={handleCloseTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          selectedTemplate={selectedTemplate}
          templatePanelMode={templatePanelMode}
          orgAdmins={orgAdmins}
          corporates={corporates}
          onSaveOrgAdmin={handleSaveOrgAdmin}
          onDeleteOrgAdmin={handleDeleteOrgAdmin}
          isLoadingCompliance={isLoadingCompliance}
          isComplianceInitialized={isComplianceInitialized}
          frequencyOptions={FREQUENCY_OPTIONS}
          timelineTypes={TIMELINE_TYPES}
          onUpdateCategory={handleCategoryUpdate}
          onAddCategory={handleAddCategory}
          onRemoveCategory={handleRemoveCategory}
          onUpdateTask={handleUpdateTask}
          onAddTask={handleAddTask}
          onRemoveTask={handleRemoveTask}
        />
      )}
    </div>
  );
};

SuperComplianceDashboard.propTypes = {
  activeView: PropTypes.oneOf([...ALLOWED_VIEWS]),
};

SuperComplianceDashboard.defaultProps = {
  activeView: "summary",
};

export default SuperComplianceDashboard;
